import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Quill from 'quill';
import MagicUrl from 'quill-magic-url';
import ImageUploader from 'quill2-image-uploader';
import { tracked } from '@glimmer/tracking';
import imageResizeOptions from 'scaleflow/utils/quill-extenstions/image-resize';
import ImageFormat from 'scaleflow/utils/quill-extenstions/image-format';
import { hrIcon } from 'scaleflow/utils/quill-extenstions/custom-icons';
import Hr from 'scaleflow/utils/quill-extenstions/hr';
import registerIfNotRegistered from 'scaleflow/utils/quill-extenstions/registerIfNotRegistered';
import imageUploadHandler from 'scaleflow/utils/quill-extenstions/image-uploader';
import { hideAll } from 'tippy.js';
import dateSort from 'scaleflow/utils/dateSort';
import textDeltaIsEmpty from 'scaleflow/utils/text-delta-is-empty';
import { initiative_doc_template } from 'scaleflow/utils/initiative-template';

Quill.register('formats/image', ImageFormat, true);
Quill.register('modules/magicUrl', MagicUrl, true);

registerIfNotRegistered(Quill, 'modules/imageUploader', ImageUploader);

const icons = Quill.import('ui/icons');

icons.hr = hrIcon;

Quill.register({
  'formats/hr': Hr,
});

export default class RoadmapTableInitiativeDocumentController extends Controller {
  @service router;
  @service('quill') quillService;
  @service('beta-toggle') betaService;
  @service store;
  @service('currentUser') currentUserService;
  @service initiativeSlideover;
  @service intl;
  @service session;
  @service organisation;

  @tracked isLoading = false;
  @tracked editMode = false;
  @tracked slideoverIsOpen = false;

  @tracked documentVersions = [];
  @tracked selectedVersion = null;
  @tracked showNotification = false;

  latestVersion = null;
  notificationTimeout = null;
  initiativeDocVersionAdapter = this.store.adapterFor(
    'initiative-document-version',
  );

  originalTextDelta = null;

  editor = null;

  timeout = 3000;

  editorId = 'initiative-document';

  get modules() {
    return {
      toolbar: {
        container: [
          ['bold', 'italic', 'strike'], // toggled buttons
          ['blockquote', 'code-block'],
          [{ header: 1 }, { header: 2 }], // custom button values
          [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
          ['link', 'clean'], // remove formatting button
          ['hr'],
        ],
        handlers: {
          hr: this.customHrHandler,
        },
        magicUrl: true,
      },
    };
  }

  get theme() {
    return 'bubble';
  }

  get disabledEditorInstance() {
    return this.quillService.instance(this.editorId);
  }

  get text_delta() {
    return this.model.text_delta;
  }

  options = {
    placeholder: this.intl.t('_initiatives.start-here'),
    theme: 'bubble',
    bounds: `#${this.editorId}`,
    // formats: ['style'],
    modules: {
      toolbar: {
        container: [
          ['bold', 'italic', 'strike'], // toggled buttons
          ['blockquote', 'code-block'],
          [{ header: 1 }, { header: 2 }], // custom button values
          [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
          ['link', 'clean', 'image'], // remove formatting button
          ['hr'],
        ],
        handlers: {
          hr: this.customHrHandler,
          // image: this.imageHandler,
        },
      },
      imageUploader: {
        upload: (file) => {
          return imageUploadHandler(
            this.session.idToken,
            this.organisation.id,
            file,
          ).then((s3Link) => {
            return s3Link;
          });
        },
      },
      resize: imageResizeOptions(this.intl),
      magicUrl: true,
    },
  };

  totalPeers = null;

  get versions() {
    return this.store
      .peekAll('initiative-document-version')
      .filter((item) => item.get('initiative.id') === this.model.id)
      .sort((a, b) => dateSort(a.created_at, b.created_at))
      .reverse();
  }

  @action
  handleSlideoverOpen() {
    this.openSlideover();
  }

  @action
  toggleShowNotification() {
    clearTimeout(this.notificationTimeout);
    this.showNotification = !this.showNotification;
    if (this.showNotification === true)
      this.notificationTimeout = setTimeout(
        () => this.toggleShowNotification(),
        5000,
      );
  }

  @action
  openSlideover() {
    this.slideoverIsOpen = true;
    this.hideTippy();
  }

  @action
  closeSlideover() {
    this.slideoverIsOpen = false;
  }

  @action
  hideTippy() {
    hideAll();
  }

  @action
  onTextChange() {
    this.model.text_delta = this.editor.getContents();
  }

  @action
  onEditorChange(e) {
    if (!this.selectedVersion) this.model.text_delta = e;
  }

  @action
  customHrHandler() {
    // get the position of the cursor
    const range = this.editor.getSelection();
    if (range) {
      // insert the <hr> where the cursor is
      this.editor.insertEmbed(range.index, 'hr', 'null');
    }
  }

  @action
  saveDoc() {
    this.model.save();
  }

  @action
  async toggleEditMode() {
    if (this.editMode === true) {
      this.model.save();
    }
    //it was read only
    if (this.editMode === false) {
      await this.createVersion();
    }

    this.editMode = !this.editMode;
  }

  @action
  cancelEdit() {
    this.model.text_delta = this.latestVersion.text_delta;
    this.editMode = !this.editMode;
  }

  @action
  async createVersion() {
    const document = this.store.createRecord('initiative-document-version');
    document.initiative = this.model;
    document.text_delta = this.model.text_delta;
    document.save();
    this.latestVersion = document;
  }

  @action
  loadVersionsList() {
    this.model.reload({ include: 'workstream' });
  }

  @action
  async restoreVersion(version) {
    await this.initiativeDocVersionAdapter.restoreVersion({
      version_id: version.id,
      initiative_id: this.model.id,
    });
    this.model.text_delta = version.text_delta;
    this.closeSlideover();
    this.selectedVersion = null;
    this.originalTextDelta = this.model.text_delta;
    this.loadVersionsList();
    this.toggleShowNotification();
  }

  @action
  initiateEditor() {
    setTimeout(() => {
      const editorInstance = this.quillService.instance(this.editorId);
      editorInstance.clipboard.addMatcher('IMG', () => {
        const Delta = Quill.import('delta');
        return new Delta().insert('');
      });
      editorInstance.clipboard.addMatcher('PICTURE', () => {
        const Delta = Quill.import('delta');
        return new Delta().insert('');
      });

      this.editor = editorInstance;
      this.originalTextDelta = this.model.text_delta;
    }, 0);
  }

  @action
  populateEditorWithSelectedVersion() {
    if (this.selectedVersion) {
      this.model.text_delta = this.selectedVersion.text_delta;
    } else {
      this.model.text_delta = this.originalTextDelta;
    }
  }

  @action
  onVersionItemClick(version) {
    this.selectedVersion = version;
    this.editMode = false;
  }

  @action
  restoreToCurrentVersion() {
    this.selectedVersion = null;
  }

  @action
  handleSettingsSlideoverOpen() {
    this.initiativeSlideover.open(this.model.id);
  }

  get showLoadTemplateButton() {
    return this.editMode && textDeltaIsEmpty(this.text_delta);
  }

  @action
  loadTemplate() {
    this.model.text_delta = initiative_doc_template;
  }
}
